import React from "react";
import { Box, Typography } from "@mui/material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import StatCard from "./StatCard";
import { fmtK, fmtPct } from "../utils";

export default function BudgetSummary({
  totalBudget,
  onEditBudget,
  readOnly = false, // a channel's view: its budget, not editable here
  allocated,
  variance,
  planVsLYPct,
  planVsDelta,
  // Next-year plans: e.g. "Oct–Dec" when LY includes forecast months.
  lyForecastRange = "",
  totalYTDActual = 0,
  ytdPlanApprox = 0,
  totalYearProj = 0,
  currentYearData,
  totalSubcategories,
  allLockedCount,
  lockedAllocated, // sum of locked category plan values
  darkMode,
}) {
  const textSecondary = darkMode ? "#aaa" : "#888";

  // YTD Actual vs pro-rated plan
  const ytdDelta = totalYTDActual - ytdPlanApprox;
  const ytdDeltaPct =
    ytdPlanApprox > 0 ? (ytdDelta / ytdPlanApprox) * 100 : null;

  // Year Projection vs plan
  const projDelta = totalYearProj - allocated;
  const projDeltaPct = allocated > 0 ? (projDelta / allocated) * 100 : null;

  // Determine alert state
  const alertMsg = (() => {
    if (
      lockedAllocated != null &&
      lockedAllocated > totalBudget &&
      totalBudget > 0
    ) {
      return `Locked allocations (${fmtK(lockedAllocated)}) exceed budget cap (${fmtK(totalBudget)}). Unlock rows or raise the cap.`;
    }
    if (
      allLockedCount > 0 &&
      allLockedCount === totalSubcategories &&
      allocated !== totalBudget
    ) {
      return "All rows locked. Unlock at least one to rebalance.";
    }
    if (allocated > totalBudget + 1000) {
      return `Plan exceeds budget by ${fmtK(allocated - totalBudget)}.`;
    }
    return null;
  })();

  return (
    <>
      {/* ── Budget summary cards (6, equal width) ── */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr 1fr",
            sm: "repeat(3, 1fr)",
            lg: "repeat(6, minmax(0, 1fr))",
          },
          gap: 1.5,
          mb: 2,
        }}
      >
        <StatCard
          label="Total Budget"
          value={totalBudget}
          sub={readOnly ? "This channel's share of the plan" : `CY${currentYearData} OTB cap · click to edit`}
          editable={!readOnly}
          onEdit={onEditBudget}
          darkMode={darkMode}
        />
        <StatCard
          label="Allocated"
          value={allocated}
          sub={
            totalBudget > 0
              ? `${Math.round((allocated / totalBudget) * 100)}% of cap`
              : "–"
          }
          darkMode={darkMode}
        />
        <StatCard
          label="Variance"
          value={Math.abs(variance)}
          sub={
            variance === 0
              ? "balanced"
              : variance > 0
                ? `${fmtK(variance)} unallocated`
                : `${fmtK(Math.abs(variance))} over budget`
          }
          subColor={
            variance === 0 ? "#2e7d32" : variance < 0 ? "#c62828" : "#ed6c02"
          }
          darkMode={darkMode}
        />
        <StatCard
          label="Plan vs LY"
          value={Math.abs(planVsDelta)}
          sub={
            planVsLYPct != null
              ? `${fmtPct(planVsLYPct)} · ${fmtK(Math.abs(planVsDelta))} ${planVsDelta >= 0 ? "growth" : "decline"}${lyForecastRange ? ` · LY incl. ${lyForecastRange} forecast` : ""}`
              : "–"
          }
          subColor={planVsDelta >= 0 ? "#2e7d32" : "#c62828"}
          darkMode={darkMode}
        />
        <StatCard
          label="YTD Actual"
          value={totalYTDActual}
          sub={
            ytdDeltaPct != null
              ? `${ytdDelta >= 0 ? "+" : ""}${fmtK(ytdDelta)} · ${ytdDelta >= 0 ? "+" : ""}${ytdDeltaPct.toFixed(1)}% vs plan`
              : "–"
          }
          subColor={ytdDelta >= 0 ? "#2e7d32" : "#c62828"}
          darkMode={darkMode}
        />
        <StatCard
          label="Year Projection"
          value={totalYearProj}
          sub={
            projDeltaPct != null
              ? `${projDelta >= 0 ? "+" : ""}${fmtK(projDelta)} · ${projDelta >= 0 ? "+" : ""}${projDeltaPct.toFixed(1)}% vs plan`
              : "–"
          }
          subColor={projDelta >= 0 ? "#2e7d32" : "#c62828"}
          darkMode={darkMode}
        />
      </Box>

      {/* ── Allocation progress bar ── */}
      <Box sx={{ mb: 2 }}>
        <Box
          sx={{
            height: 6,
            borderRadius: 3,
            bgcolor: darkMode ? "#333" : "#e0e0e0",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              height: "100%",
              width: `${Math.min(100, totalBudget > 0 ? (allocated / totalBudget) * 100 : 0)}%`,
              background: "linear-gradient(90deg, #ff8f00, #ffa000)",
              borderRadius: 3,
              transition: "width 0.4s ease",
            }}
          />
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
          <Typography sx={{ fontSize: 11, color: textSecondary }}>
            {fmtK(allocated)} of {fmtK(totalBudget)} allocated
          </Typography>
          <Typography sx={{ fontSize: 11, color: textSecondary }}>
            {totalSubcategories} sub-categories · {allLockedCount} locked
          </Typography>
        </Box>
      </Box>

      {/* ── Alert band ── */}
      {alertMsg && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1.75,
            py: 1.125,
            mb: 2,
            bgcolor: darkMode ? "#2a1414" : "#FEF2F2",
            border: "1px solid rgba(220,38,38,0.18)",
            borderRadius: 2,
            color: "#DC2626",
            fontSize: 12,
          }}
        >
          <WarningAmberRoundedIcon sx={{ fontSize: 15, flexShrink: 0 }} />
          <Typography sx={{ fontSize: 12, color: "#DC2626" }}>
            {alertMsg}
          </Typography>
        </Box>
      )}
    </>
  );
}
